import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, Polygon, useMap, Circle, Pane } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  MapPin, Search, Calendar, Filter, X, RefreshCw, Layers, 
  Users, AlertTriangle, Download, ChevronDown, ChevronUp,
  Plus, Trash2, List, Map as MapIcon, Pencil, Sparkles
} from 'lucide-react';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const createIcon = (color) => new L.Icon({
  iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const disciplineIcons = {
  'PT': createIcon('red'),
  'OT': createIcon('blue'),
  'ST': createIcon('green'),
  'SN': createIcon('orange'),
  'HHA': createIcon('violet'),
  'default': createIcon('grey'),
};

const getDisciplineIcon = (discipline) => {
  const upper = (discipline || '').toUpperCase();
  return disciplineIcons[upper] || disciplineIcons.default;
};

const coverageColors = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
];

function FitBounds({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (positions.length > 0) {
      const bounds = L.latLngBounds(positions.map(p => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 10 });
    }
  }, [positions, map]);
  return null;
}

export default function DeclinationsMap({ embedded = false }) {
  const [declinations, setDeclinations] = useState([]);
  const [allDeclinations, setAllDeclinations] = useState([]);
  const [applicantCoverages, setApplicantCoverages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  
  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedDiscipline, setSelectedDiscipline] = useState('all');
  const [selectedAgency, setSelectedAgency] = useState('all');
  const [disciplines, setDisciplines] = useState([]);
  const [agencies, setAgencies] = useState([]);
  
  // Layer visibility
  const [showDeclinations, setShowDeclinations] = useState(true);
  const [showApplicantCoverage, setShowApplicantCoverage] = useState(false);
  const [expandedFilters, setExpandedFilters] = useState(true);
  const [geocoding, setGeocoding] = useState(false);

  // View toggle: 'map' or 'list'
  const [activeView, setActiveView] = useState('map');

  // Add form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [addingDeclination, setAddingDeclination] = useState(false);
  const [newDec, setNewDec] = useState({
    address: '', agency: '', date: new Date().toISOString().split('T')[0], disciplines: []
  });

  // Agency list for dropdown
  const [agencyList, setAgencyList] = useState([]);

  // Selection for bulk delete
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  // Edit state
  const [editingDec, setEditingDec] = useState(null);
  const [editForm, setEditForm] = useState({ address: '', agency: '', date: '', discipline: '' });
  const [saving, setSaving] = useState(false);

  // List pagination/search
  const [listSearch, setListSearch] = useState('');
  const [listPage, setListPage] = useState(1);
  const PAGE_SIZE = 25;

  // Hiring demand (ZIP / territory rollup)
  const [hiringReport, setHiringReport] = useState(null);
  const [hiringLoading, setHiringLoading] = useState(false);
  const [hiringTab, setHiringTab] = useState('territory');
  const [reportDiscipline, setReportDiscipline] = useState('PT');
  const [exportingCsv, setExportingCsv] = useState(false);
  const [catchingUp, setCatchingUp] = useState(false);

  const hiringParams = () => {
    const p = {};
    if (reportDiscipline && reportDiscipline !== 'all') p.discipline = reportDiscipline;
    if (startDate) p.start_date = startDate;
    if (endDate) p.end_date = endDate;
    if (selectedAgency && selectedAgency !== 'all') p.agency = selectedAgency;
    return p;
  };

  const fetchHiringReport = async () => {
    setHiringLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/declinations/hiring-report`, {
        params: hiringParams(),
        headers: { Authorization: `Bearer ${token}` },
      });
      setHiringReport(response.data);
    } catch (error) {
      console.error('Error fetching hiring report:', error);
    } finally {
      setHiringLoading(false);
    }
  };

  const handleExportHiringCsv = async () => {
    setExportingCsv(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/declinations/hiring-report.csv`, {
        params: hiringParams(),
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `hiring-report-${reportDiscipline}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Hiring CSV exported');
    } catch (error) {
      toast.error('Failed to export CSV');
    } finally {
      setExportingCsv(false);
    }
  };

  // Optional: loop the batched geocode endpoint until nothing remains (rate-limit safe).
  const handleCatchUpCoords = async () => {
    setCatchingUp(true);
    let totalGeocoded = 0;
    let guard = 0;
    try {
      const token = localStorage.getItem('token');
      while (guard < 100) {
        guard += 1;
        const res = await axios.post(`${API}/declinations/geocode`, {}, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 120000,
        });
        const d = res.data || {};
        totalGeocoded += d.geocoded || 0;
        const remaining = d.remaining || 0;
        if ((d.geocoded || 0) === 0 && remaining > 0) break; // no progress guard
        if (remaining > 0) {
          toast.message(`Geocoded ${totalGeocoded} so far · ${remaining} remaining…`);
          await new Promise(r => setTimeout(r, 1200)); // be gentle on Nominatim
        } else {
          break;
        }
      }
      toast.success(`Caught up map coordinates · ${totalGeocoded} geocoded`);
      fetchDeclinations();
      fetchStats();
    } catch (error) {
      toast.error(`Geocode catch-up paused after ${totalGeocoded}. Click again to continue.`);
      fetchStats();
    } finally {
      setCatchingUp(false);
    }
  };

  useEffect(() => {
    fetchDeclinations();
    fetchAllDeclinations();
    fetchMetadata();
    fetchStats();
    fetchApplicantCoverages();
    fetchAgencyList();
    fetchHiringReport();
  }, []);

  const fetchDeclinations = async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (selectedDiscipline && selectedDiscipline !== 'all') params.discipline = selectedDiscipline;
      if (selectedAgency && selectedAgency !== 'all') params.agency = selectedAgency;
      const response = await axios.get(`${API}/declinations/map-data`, { params });
      setDeclinations(response.data);
    } catch (error) {
      console.error('Error fetching declinations:', error);
      toast.error('Failed to load declinations');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllDeclinations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/declinations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllDeclinations(response.data);
    } catch (error) {
      console.error('Error fetching all declinations:', error);
    }
  };

  const fetchMetadata = async () => {
    try {
      const [discRes, agencyRes] = await Promise.all([
        axios.get(`${API}/declinations/disciplines`),
        axios.get(`${API}/declinations/agencies`)
      ]);
      setDisciplines(discRes.data.disciplines || []);
      setAgencies(agencyRes.data.agencies || []);
    } catch (error) {
      console.error('Error fetching metadata:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/declinations/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchApplicantCoverages = async () => {
    try {
      const response = await axios.get(`${API}/ats/coverage/all`);
      setApplicantCoverages(response.data);
    } catch (error) {
      console.error('Error fetching coverage:', error);
    }
  };

  const fetchAgencyList = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/agency-profiles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const names = (response.data || [])
        .map(a => a.name || a.agency_name || '')
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
      setAgencyList(names);
    } catch (error) {
      console.error('Error fetching agency list:', error);
    }
  };

  const handleGeocode = async () => {
    setGeocoding(true);
    try {
      const response = await axios.post(`${API}/declinations/geocode`);
      toast.success(`Geocoded ${response.data.geocoded} declinations. ${response.data.remaining} remaining.`);
      fetchDeclinations();
      fetchStats();
    } catch (error) {
      toast.error('Geocoding failed - try again later');
    } finally {
      setGeocoding(false);
    }
  };

  const handleSearch = () => {
    fetchDeclinations();
    fetchHiringReport();
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSelectedDiscipline('all');
    setSelectedAgency('all');
    fetchDeclinations();
  };

  // Add declination
  const handleAddDeclination = async (e) => {
    e.preventDefault();
    if (!newDec.address || newDec.disciplines.length === 0 || !newDec.date) {
      toast.error('Address, at least one discipline, and date are required');
      return;
    }
    setAddingDeclination(true);
    try {
      const token = localStorage.getItem('token');
      for (const disc of newDec.disciplines) {
        await axios.post(`${API}/declinations`, {
          address: newDec.address,
          agency: newDec.agency,
          date: newDec.date,
          discipline: disc
        }, { headers: { Authorization: `Bearer ${token}` } });
      }
      toast.success(`Added ${newDec.disciplines.length} declination(s)`);
      setNewDec({ address: '', agency: '', date: new Date().toISOString().split('T')[0], disciplines: [] });
      setShowAddForm(false);
      fetchDeclinations();
      fetchAllDeclinations();
      fetchStats();
      fetchMetadata();
    } catch (error) {
      toast.error('Failed to add declination');
    } finally {
      setAddingDeclination(false);
    }
  };

  // Delete single
  const handleDeleteOne = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/declinations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Declination removed');
      fetchDeclinations();
      fetchAllDeclinations();
      fetchStats();
      setSelectedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
    } catch (error) {
      toast.error('Failed to delete declination');
    }
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/declinations/bulk-delete`, 
        { ids: Array.from(selectedIds) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Removed ${res.data.deleted_count} declination(s)`);
      setSelectedIds(new Set());
      fetchDeclinations();
      fetchAllDeclinations();
      fetchStats();
      fetchMetadata();
    } catch (error) {
      toast.error('Failed to delete declinations');
    } finally {
      setDeleting(false);
    }
  };

  // Start editing a declination
  const startEdit = (dec) => {
    setEditingDec(dec.id);
    setEditForm({
      address: dec.address || '',
      agency: dec.agency || '',
      date: dec.date || '',
      discipline: dec.discipline || ''
    });
  };

  // Save edit
  const handleSaveEdit = async () => {
    if (!editForm.address || !editForm.discipline || !editForm.date) {
      toast.error('Address, discipline, and date are required');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/declinations/${editingDec}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Declination updated');
      setEditingDec(null);
      fetchDeclinations();
      fetchAllDeclinations();
      fetchStats();
    } catch (error) {
      toast.error('Failed to update declination');
    } finally {
      setSaving(false);
    }
  };

  // Toggle select
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  // Select all visible
  const toggleSelectAll = () => {
    if (selectedIds.size === filteredList.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredList.map(d => d.id)));
    }
  };

  // Filtered & paginated list
  const filteredList = useMemo(() => {
    let list = allDeclinations;
    if (listSearch) {
      const q = listSearch.toLowerCase();
      list = list.filter(d =>
        (d.address || '').toLowerCase().includes(q) ||
        (d.agency || '').toLowerCase().includes(q) ||
        (d.discipline || '').toLowerCase().includes(q) ||
        (d.city || '').toLowerCase().includes(q) ||
        (d.date || '').includes(q)
      );
    }
    return list;
  }, [allDeclinations, listSearch]);

  const paginatedList = useMemo(() => {
    const start = (listPage - 1) * PAGE_SIZE;
    return filteredList.slice(start, start + PAGE_SIZE);
  }, [filteredList, listPage]);

  const totalPages = Math.ceil(filteredList.length / PAGE_SIZE);

  const positions = useMemo(() => {
    return declinations
      .filter(d => d.latitude && d.longitude)
      .map(d => ({ lat: d.latitude, lng: d.longitude }));
  }, [declinations]);

  return (
    <div className={embedded ? "space-y-4" : "p-6 space-y-6"}>
      {!embedded && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Declinations</h1>
            <p className="text-muted-foreground mt-1">
              View and manage locations where visits were declined
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant={showAddForm ? "default" : "outline"}
              onClick={() => setShowAddForm(!showAddForm)}
              data-testid="add-declination-btn"
            >
              <Plus size={16} className="mr-1" />
              Add Declination
            </Button>
            <Button
              variant="outline"
              onClick={handleGeocode}
              disabled={geocoding}
            >
              <RefreshCw size={16} className={geocoding ? 'animate-spin' : ''} />
              {geocoding ? 'Geocoding...' : 'Update Coords'}
            </Button>
            <Button
              variant="outline"
              onClick={handleCatchUpCoords}
              disabled={catchingUp}
              data-testid="catch-up-coords-btn"
              title="Geocode all remaining declinations in rate-limit-safe batches"
            >
              <RefreshCw size={16} className={catchingUp ? 'animate-spin mr-1' : 'mr-1'} />
              {catchingUp ? 'Catching up…' : 'Catch up map coords'}
            </Button>
            <Link to="/upload-declinations">
              <Button variant="outline">Upload CSV</Button>
            </Link>
            <Link to="/admin/declinations-review">
              <Button data-testid="declinations-ai-parser-link">
                <Sparkles size={16} className="mr-1" />
                AI Email Parser
              </Button>
            </Link>
          </div>
        </div>
      )}

      {embedded && (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground">View locations where visits were declined</p>
          <div className="flex gap-2">
            <Button variant={showAddForm ? "default" : "outline"} size="sm" onClick={() => setShowAddForm(!showAddForm)}>
              <Plus size={14} className="mr-1" /> Add
            </Button>
            <Button variant="outline" size="sm" onClick={handleGeocode} disabled={geocoding}>
              <RefreshCw size={14} className={geocoding ? 'animate-spin' : ''} />
            </Button>
            <Link to="/upload-declinations">
              <Button variant="outline" size="sm">Upload</Button>
            </Link>
          </div>
        </div>
      )}

      {/* Add Declination Form */}
      {showAddForm && (
        <Card className="p-5 border-primary/30 bg-primary/5">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Plus size={18} />
            Add New Declination
          </h3>
          <form onSubmit={handleAddDeclination} className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs mb-1 block">Address *</Label>
              <Input
                placeholder="123 Main St, City, State ZIP"
                value={newDec.address}
                onChange={(e) => setNewDec({ ...newDec, address: e.target.value })}
                data-testid="new-dec-address"
                required
              />
            </div>
            <div>
              <Label className="text-xs mb-1 block">Agency</Label>
              <Select value={newDec.agency} onValueChange={(v) => setNewDec({ ...newDec, agency: v })}>
                <SelectTrigger data-testid="new-dec-agency">
                  <SelectValue placeholder="Select agency" />
                </SelectTrigger>
                <SelectContent>
                  {agencyList.map(name => (
                    <SelectItem key={name} value={name}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Date *</Label>
              <Input
                type="date"
                value={newDec.date}
                onChange={(e) => setNewDec({ ...newDec, date: e.target.value })}
                data-testid="new-dec-date"
                required
              />
            </div>
            <div>
              <Label className="text-xs mb-1 block">Discipline * (select one or more)</Label>
              <div className="flex items-center gap-4 h-9">
                {['PT', 'OT', 'ST'].map(d => (
                  <label key={d} className="flex items-center gap-1.5 cursor-pointer">
                    <Checkbox
                      checked={newDec.disciplines.includes(d)}
                      onCheckedChange={(checked) => {
                        setNewDec(prev => ({
                          ...prev,
                          disciplines: checked
                            ? [...prev.disciplines, d]
                            : prev.disciplines.filter(x => x !== d)
                        }));
                      }}
                      data-testid={`new-dec-disc-${d.toLowerCase()}`}
                    />
                    <span className="text-sm font-medium">{d}</span>
                  </label>
                ))}
                <Button type="submit" disabled={addingDeclination} className="ml-auto" data-testid="submit-declination-btn">
                  {addingDeclination ? <RefreshCw size={16} className="animate-spin" /> : 'Add'}
                </Button>
              </div>
            </div>
          </form>
        </Card>
      )}
      
      {/* Warning if some declinations don't have coordinates */}
      {stats && stats.missing_coordinates > 0 && (
        <Card className="p-4 bg-amber-50 border-amber-200">
          <div className="flex items-center gap-3">
            <AlertTriangle className="text-amber-600" size={20} />
            <div>
              <p className="font-medium text-amber-800">
                {stats.missing_coordinates} of {stats.total} declinations don't have map coordinates
              </p>
              <p className="text-sm text-amber-600">
                Click "Update Coords" to geocode addresses and display them on the map.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">On Map / Total</p>
                <p className="text-2xl font-bold">
                  <span className="text-primary">{stats.with_coordinates || 0}</span>
                  <span className="text-muted-foreground"> / {stats.total}</span>
                </p>
              </div>
              <MapPin className="text-primary" size={24} />
            </div>
          </Card>
          {stats.by_discipline?.slice(0, 3).map((d, i) => (
            <Card key={i} className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{d.discipline}</p>
                  <p className="text-2xl font-bold">{d.count}</p>
                </div>
                <Badge variant="outline">{((d.count / stats.total) * 100).toFixed(0)}%</Badge>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Hiring demand — By ZIP / Territory */}
      <Card className="p-5" data-testid="hiring-demand-card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="font-semibold flex items-center gap-2">
              <MapPin size={18} className="text-primary" /> Hiring demand — By ZIP / Territory
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Ranked declination demand for hiring. Counts every referral with a ZIP — no wait for map geocoding.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={reportDiscipline} onValueChange={(v) => { setReportDiscipline(v); setTimeout(fetchHiringReport, 0); }}>
              <SelectTrigger className="h-9 w-[130px]" data-testid="hiring-discipline-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PT">PT</SelectItem>
                <SelectItem value="OT">OT</SelectItem>
                <SelectItem value="ST">ST</SelectItem>
                <SelectItem value="all">All disciplines</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={fetchHiringReport} disabled={hiringLoading} data-testid="hiring-refresh-btn">
              <RefreshCw size={14} className={hiringLoading ? 'animate-spin' : ''} />
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportHiringCsv} disabled={exportingCsv} data-testid="hiring-export-csv-btn">
              <Download size={14} className="mr-1" /> {exportingCsv ? 'Exporting…' : 'Export CSV'}
            </Button>
          </div>
        </div>

        {/* Totals */}
        {hiringReport && (
          <div className="flex flex-wrap gap-4 mb-4 text-sm">
            <span data-testid="hiring-total"><span className="font-semibold text-lg">{hiringReport.total}</span> <span className="text-muted-foreground">total</span></span>
            <span data-testid="hiring-with-zip"><span className="font-semibold text-lg text-primary">{hiringReport.with_zip}</span> <span className="text-muted-foreground">with ZIP</span></span>
            <span data-testid="hiring-missing-coords"><span className="font-semibold text-lg text-amber-600">{hiringReport.missing_coordinates}</span> <span className="text-muted-foreground">not yet on map</span></span>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-3">
          <Button variant={hiringTab === 'territory' ? 'default' : 'outline'} size="sm" onClick={() => setHiringTab('territory')} data-testid="hiring-tab-territory">
            By Territory
          </Button>
          <Button variant={hiringTab === 'zip' ? 'default' : 'outline'} size="sm" onClick={() => setHiringTab('zip')} data-testid="hiring-tab-zip">
            By ZIP
          </Button>
        </div>

        {hiringLoading ? (
          <div className="py-8 text-center text-muted-foreground text-sm"><RefreshCw className="animate-spin mx-auto mb-2" size={20} /> Loading demand…</div>
        ) : !hiringReport || hiringReport.total === 0 ? (
          <div className="py-8 text-center text-muted-foreground text-sm">No declinations match these filters.</div>
        ) : hiringTab === 'territory' ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="hiring-territory-table">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-2 text-left">Houston sub-area</th>
                  <th className="px-4 py-2 text-right">Declines</th>
                  <th className="px-4 py-2 text-left">ZIPs</th>
                </tr>
              </thead>
              <tbody>
                {hiringReport.by_territory.map((t, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-4 py-2 font-medium">{t.territory}</td>
                    <td className="px-4 py-2 text-right font-semibold">{t.count}</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground max-w-md truncate" title={t.zips.join(', ')}>{t.zips.join(', ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="hiring-zip-table">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-2 text-left">ZIP</th>
                  <th className="px-4 py-2 text-left">City / State</th>
                  <th className="px-4 py-2 text-right">Declines</th>
                  <th className="px-4 py-2 text-right">On map</th>
                  <th className="px-4 py-2 text-right">Pending geocode</th>
                </tr>
              </thead>
              <tbody>
                {hiringReport.by_zip.slice(0, 50).map((z, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-4 py-2 font-medium">{z.zip}</td>
                    <td className="px-4 py-2 text-muted-foreground">{[z.city, z.state].filter(Boolean).join(', ') || '—'}</td>
                    <td className="px-4 py-2 text-right font-semibold">{z.count}</td>
                    <td className="px-4 py-2 text-right text-muted-foreground">{z.geocoded}</td>
                    <td className="px-4 py-2 text-right text-amber-600">{z.ungeocoded}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* View Toggle */}
      <div className="flex gap-2">
        <Button
          variant={activeView === 'map' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveView('map')}
          data-testid="map-view-btn"
        >
          <MapIcon size={16} className="mr-1" /> Map View
        </Button>
        <Button
          variant={activeView === 'list' ? 'default' : 'outline'}
          size="sm"
          onClick={() => { setActiveView('list'); setListPage(1); }}
          data-testid="list-view-btn"
        >
          <List size={16} className="mr-1" /> List View ({allDeclinations.length})
        </Button>
      </div>

      {/* === MAP VIEW === */}
      {activeView === 'map' && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Filters Panel */}
          <Card className="p-4 lg:col-span-1">
            <div 
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setExpandedFilters(!expandedFilters)}
            >
              <h3 className="font-semibold flex items-center gap-2">
                <Filter size={18} /> Filters & Layers
              </h3>
              {expandedFilters ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </div>
            {expandedFilters && (
              <div className="mt-4 space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Date Range</Label>
                  <div className="flex gap-2">
                    <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="text-sm w-[130px]" data-testid="start-date" />
                    <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="text-sm w-[130px]" data-testid="end-date" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Discipline</Label>
                  <Select value={selectedDiscipline} onValueChange={setSelectedDiscipline}>
                    <SelectTrigger data-testid="discipline-filter">
                      <SelectValue placeholder="All Disciplines" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Disciplines</SelectItem>
                      {disciplines.map(d => (<SelectItem key={d} value={d}>{d}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Agency</Label>
                  <Select value={selectedAgency} onValueChange={setSelectedAgency}>
                    <SelectTrigger data-testid="agency-filter">
                      <SelectValue placeholder="All Agencies" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Agencies</SelectItem>
                      {agencies.map(a => (<SelectItem key={a} value={a}>{a}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleSearch} className="flex-1" data-testid="search-btn">
                    <Search size={16} className="mr-2" /> Search
                  </Button>
                  <Button variant="outline" onClick={clearFilters}><X size={16} /></Button>
                </div>
                <hr className="my-4" />
                <div className="space-y-3">
                  <h4 className="font-medium text-sm flex items-center gap-2"><Layers size={16} /> Map Layers</h4>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="show-declinations" className="text-sm">Declinations</Label>
                    <Switch id="show-declinations" checked={showDeclinations} onCheckedChange={setShowDeclinations} />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="show-coverage" className="text-sm">Applicant Coverage Areas</Label>
                    <Switch id="show-coverage" checked={showApplicantCoverage} onCheckedChange={setShowApplicantCoverage} />
                  </div>
                </div>
                <div className="mt-4 p-3 bg-muted rounded-lg">
                  <h4 className="font-medium text-sm mb-2">Legend</h4>
                  <div className="space-y-1 text-xs">
                    <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-red-500" /><span>PT</span></div>
                    <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-blue-500" /><span>OT</span></div>
                    <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-green-500" /><span>ST</span></div>
                    <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-orange-500" /><span>SN</span></div>
                  </div>
                </div>
              </div>
            )}
          </Card>

          {/* Map */}
          <Card className="lg:col-span-3 overflow-hidden" style={{ height: '600px' }}>
            {loading ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <RefreshCw className="animate-spin mx-auto mb-2" size={32} />
                  <p className="text-muted-foreground">Loading map data...</p>
                </div>
              </div>
            ) : (
              <MapContainer center={[29.7604, -95.3698]} zoom={8} style={{ height: '100%', width: '100%' }}>
                {/* Base map WITHOUT labels — coverage overlays draw on top of this.
                    Esri Light Gray Canvas (no API key required). */}
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}"
                  maxNativeZoom={16}
                  maxZoom={19}
                  attribution='Tiles &copy; Esri &mdash; Esri, HERE, Garmin, &copy; OpenStreetMap contributors'
                />
                {/* Labels-only layer on a high z-index pane so town / city names
                    stay readable ON TOP of the colored coverage overlays. */}
                <Pane name="map-labels" style={{ zIndex: 620, pointerEvents: 'none' }}>
                  <TileLayer
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}"
                    maxNativeZoom={16}
                    maxZoom={19}
                    pane="map-labels"
                  />
                </Pane>
                {positions.length > 0 && <FitBounds positions={positions} />}
                {showApplicantCoverage && applicantCoverages.map((coverage, index) => {
                  const color = coverageColors[index % coverageColors.length];
                  if (coverage.coverage_type === 'radius' && coverage.center_lat && coverage.center_lng) {
                    return (
                      <Circle key={coverage.applicant_id} center={[coverage.center_lat, coverage.center_lng]} radius={(coverage.radius_miles || 25) * 1609.34} pathOptions={{ color, fillColor: color, fillOpacity: 0.2 }}>
                        <Popup><div className="text-sm"><p className="font-medium">{coverage.applicant_name}</p><p className="text-muted-foreground">{coverage.desired_position}</p><Badge variant="outline" className="mt-1">{coverage.status}</Badge></div></Popup>
                      </Circle>
                    );
                  }
                  if (coverage.polygon_coordinates?.length >= 3) {
                    return (
                      <Polygon key={coverage.applicant_id} positions={coverage.polygon_coordinates.map(p => [p.lat, p.lng])} pathOptions={{ color, fillColor: color, fillOpacity: 0.2 }}>
                        <Popup><div className="text-sm"><p className="font-medium">{coverage.applicant_name}</p><Badge variant="outline" className="mt-1">{coverage.status}</Badge></div></Popup>
                      </Polygon>
                    );
                  }
                  return null;
                })}
                {showDeclinations && declinations.length > 0 && declinations
                  .filter(d => d.latitude && d.longitude)
                  .map((dec) => (
                    <Marker key={dec.id} position={[dec.latitude, dec.longitude]} icon={getDisciplineIcon(dec.discipline)}>
                      <Popup>
                        <div className="text-sm space-y-1">
                          <p className="font-medium">{dec.address}</p>
                          <p>{dec.city}, {dec.state} {dec.zip_code}</p>
                          <div className="flex gap-2 mt-2">
                            <Badge>{dec.discipline}</Badge>
                            <Badge variant="outline">{dec.date}</Badge>
                          </div>
                          {dec.agency && <p className="text-muted-foreground">Agency: {dec.agency}</p>}
                          {dec.reason && <p className="text-xs italic">Reason: {dec.reason}</p>}
                        </div>
                      </Popup>
                    </Marker>
                  ))
                }
              </MapContainer>
            )}
          </Card>
        </div>
      )}

      {/* === LIST VIEW === */}
      {activeView === 'list' && (
        <Card className="p-5">
          {/* List header */}
          <div className="flex items-center justify-between mb-4 gap-4">
            <div className="flex items-center gap-3 flex-1">
              <Input
                placeholder="Search by address, agency, discipline, city..."
                value={listSearch}
                onChange={(e) => { setListSearch(e.target.value); setListPage(1); }}
                className="max-w-sm"
                data-testid="list-search"
              />
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                {filteredList.length} declination{filteredList.length !== 1 ? 's' : ''}
              </span>
            </div>
            {selectedIds.size > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleBulkDelete}
                disabled={deleting}
                data-testid="bulk-delete-btn"
              >
                <Trash2 size={14} className="mr-1" />
                {deleting ? 'Deleting...' : `Delete ${selectedIds.size} selected`}
              </Button>
            )}
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="declinations-table">
              <thead className="bg-muted">
                <tr>
                  <th className="px-3 py-2.5 text-left w-10">
                    <Checkbox
                      checked={filteredList.length > 0 && selectedIds.size === filteredList.length}
                      onCheckedChange={toggleSelectAll}
                      data-testid="select-all-checkbox"
                    />
                  </th>
                  <th className="px-3 py-2.5 text-left">Address</th>
                  <th className="px-3 py-2.5 text-left">Agency</th>
                  <th className="px-3 py-2.5 text-left">Discipline</th>
                  <th className="px-3 py-2.5 text-left">Date</th>
                  <th className="px-3 py-2.5 text-left">City / State</th>
                  <th className="px-3 py-2.5 text-center w-16">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedList.length === 0 ? (
                  <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">No declinations found</td></tr>
                ) : (
                  paginatedList.map((dec) => (
                    <tr key={dec.id} className={`border-t hover:bg-muted/50 transition-colors ${selectedIds.has(dec.id) ? 'bg-primary/5' : ''}`}>
                      <td className="px-3 py-2.5">
                        <Checkbox
                          checked={selectedIds.has(dec.id)}
                          onCheckedChange={() => toggleSelect(dec.id)}
                          data-testid={`select-${dec.id}`}
                        />
                      </td>
                      {editingDec === dec.id ? (
                        <>
                          <td className="px-3 py-1.5">
                            <Input
                              value={editForm.address}
                              onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                              className="h-8 text-sm"
                              data-testid="edit-address"
                            />
                          </td>
                          <td className="px-3 py-1.5">
                            <Select value={editForm.agency} onValueChange={(v) => setEditForm({ ...editForm, agency: v })}>
                              <SelectTrigger className="h-8 text-sm" data-testid="edit-agency">
                                <SelectValue placeholder="Agency" />
                              </SelectTrigger>
                              <SelectContent>
                                {agencyList.map(name => (
                                  <SelectItem key={name} value={name}>{name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-3 py-1.5">
                            <Select value={editForm.discipline} onValueChange={(v) => setEditForm({ ...editForm, discipline: v })}>
                              <SelectTrigger className="h-8 text-sm w-20" data-testid="edit-discipline">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="PT">PT</SelectItem>
                                <SelectItem value="OT">OT</SelectItem>
                                <SelectItem value="ST">ST</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-3 py-1.5">
                            <Input
                              type="date"
                              value={editForm.date}
                              onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                              className="h-8 text-sm"
                              data-testid="edit-date"
                            />
                          </td>
                          <td className="px-3 py-2.5 text-muted-foreground text-xs">
                            {[dec.city, dec.state].filter(Boolean).join(', ') || '-'}
                          </td>
                          <td className="px-3 py-1.5 text-center">
                            <div className="flex items-center gap-1 justify-center">
                              <Button size="sm" className="h-7 px-2 text-xs" onClick={handleSaveEdit} disabled={saving} data-testid="save-edit-btn">
                                {saving ? '...' : 'Save'}
                              </Button>
                              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setEditingDec(null)}>
                                Cancel
                              </Button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-3 py-2.5 font-medium max-w-xs truncate" title={dec.address}>
                            {dec.address}
                          </td>
                          <td className="px-3 py-2.5 text-muted-foreground">{dec.agency || '-'}</td>
                          <td className="px-3 py-2.5">
                            <Badge variant={dec.discipline === 'PT' ? 'destructive' : dec.discipline === 'OT' ? 'default' : 'secondary'}>
                              {dec.discipline}
                            </Badge>
                          </td>
                          <td className="px-3 py-2.5 text-muted-foreground">{dec.date}</td>
                          <td className="px-3 py-2.5 text-muted-foreground">
                            {[dec.city, dec.state].filter(Boolean).join(', ') || '-'}
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            <div className="flex items-center gap-0.5 justify-center">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                                onClick={() => startEdit(dec)}
                                data-testid={`edit-${dec.id}`}
                              >
                                <Pencil size={14} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-destructive hover:text-destructive"
                                onClick={() => handleDeleteOne(dec.id)}
                                data-testid={`delete-${dec.id}`}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <span className="text-sm text-muted-foreground">
                Page {listPage} of {totalPages}
              </span>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" disabled={listPage <= 1} onClick={() => setListPage(p => p - 1)}>
                  Previous
                </Button>
                <Button variant="outline" size="sm" disabled={listPage >= totalPages} onClick={() => setListPage(p => p + 1)}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Summary Table (by Agency) */}
      {activeView === 'map' && stats?.by_agency?.length > 0 && (
        <Card className="p-6">
          <h3 className="font-semibold mb-4">Declinations by Agency (Top 10)</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-2 text-left">Agency</th>
                  <th className="px-4 py-2 text-right">Count</th>
                  <th className="px-4 py-2 text-right">% of Total</th>
                </tr>
              </thead>
              <tbody>
                {stats.by_agency.slice(0, 10).map((agency, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-4 py-2">{agency.agency}</td>
                    <td className="px-4 py-2 text-right font-medium">{agency.count}</td>
                    <td className="px-4 py-2 text-right text-muted-foreground">
                      {((agency.count / stats.total) * 100).toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
